// PublicStatusPage — T093
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import StatusBadge from '../components/StatusBadge';
import { getPublicStatus, type PublicStatus } from '../services/api';

export default function PublicStatusPage() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<PublicStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) return;
    const load = () => {
      getPublicStatus(slug)
        .then(setData)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [slug]);

  // NOINDEX meta
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="animate-pulse bg-surface rounded-lg h-48 w-full max-w-2xl" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="bg-status-down/20 text-status-down rounded-lg px-6 py-4">
          {error || 'Status page not found'}
        </div>
      </div>
    );
  }

  const overallState = data.overallStatus === 'operational' ? 'UP'
    : data.overallStatus === 'partial_outage' ? 'DEGRADED' : 'DOWN';

  return (
    <div className="min-h-screen bg-bg-primary">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-white text-h1 mb-2">{data.title}</h1>
          <StatusBadge state={overallState} size="lg" />
        </div>

        <div className="space-y-3">
          {data.monitors.length === 0 ? (
            <div className="bg-surface rounded-lg p-6 border border-border text-center text-text-muted">
              No monitors configured for this status page.
            </div>
          ) : data.monitors.map((mon) => (
            <Link
              key={mon.id}
              to={`/status/${slug}/monitors/${mon.id}`}
              className="bg-surface rounded-lg p-4 border border-border flex items-center justify-between hover:border-text-muted transition-colors"
            >
              <div className="min-w-0">
                <span className="text-text-primary font-medium block">{mon.name}</span>
                <span className="text-text-muted text-xs truncate block">{mon.url}</span>
              </div>
              <StatusBadge state={mon.state} size="sm" />
            </Link>
          ))}
        </div>

        <p className="text-text-muted text-xs mt-8 text-center">
          Last updated: {new Date().toLocaleString()}
        </p>
      </div>
    </div>
  );
}
